// Domain and email-address intelligence for the IP and Domain Intelligence tool.
//
// PRIVACY NOTE: unlike the IP side, there is NO offline tier here. Every
// function in this file sends the domain (and, for an email address, its
// domain — never the mailbox name) to a third party. There is no public
// dataset that answers "who runs this domain's mail" the way the CIDR lists
// answer "is this a VPN", so domain lookups are always a network operation and
// the UI has to say so plainly.
//
// WHOIS itself is unusable from a browser — it is a raw TCP protocol on port
// 43, not HTTP. RDAP is its modern, JSON-over-HTTPS successor and is mandatory
// for gTLDs, which is what makes this possible at all. ccTLD coverage is
// uneven: .de and .ru, among others, still run WHOIS only and simply have no
// RDAP endpoint to query.

import { dnsQuery } from './ipIntelLive';

const LABEL = '[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?';
const DOMAIN_RE = new RegExp(`^(?:${LABEL}\\.)+[a-z]{2,63}$`, 'i');
const EMAIL_RE = new RegExp(`^[^\\s@,;:<>()[\\]\\\\"]+@((?:${LABEL}\\.)+[a-z]{2,63})$`, 'i');

export function isEmailAddress(value) {
  return EMAIL_RE.test(String(value).trim());
}

export function isDomainName(value) {
  const s = String(value).trim();
  // A dotted quad is a valid "domain" by shape but belongs in the IP mode.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(s)) return false;
  return DOMAIN_RE.test(s);
}

// Strips the parts people paste around a hostname: scheme, credentials, port,
// path, query and a trailing root dot.
function normalizeHost(raw) {
  let s = String(raw).trim().toLowerCase();
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');   // scheme
  s = s.replace(/^mailto:/, '');
  s = s.split(/[/?#]/)[0];                         // path/query/fragment
  s = s.replace(/^[^@]*@/, '');                    // userinfo (not an email — see caller)
  s = s.replace(/:\d+$/, '');                      // port
  s = s.replace(/\.$/, '');                        // root dot
  return s;
}

// Pulls domains and email addresses out of pasted text, preserving input order
// and de-duplicating. An email keeps its mailbox for display, but only the
// domain is ever sent anywhere.
export function parseDomainList(text) {
  const seen = new Set();
  const entries = [];
  let duplicateCount = 0;

  for (const rawToken of String(text || '').split(/[\s,;"'|<>()[\]{}]+/)) {
    if (!rawToken) continue;
    const token = rawToken.replace(/^mailto:/i, '').replace(/[.,;:]+$/, '');
    if (!token) continue;

    let entry = null;
    const email = EMAIL_RE.exec(token);
    if (email) {
      entry = {
        kind: 'email',
        input: token,
        domain: email[1].toLowerCase().replace(/\.$/, ''),
        mailbox: token.slice(0, token.lastIndexOf('@')),
      };
    } else {
      const host = normalizeHost(token);
      if (isDomainName(host)) entry = { kind: 'domain', input: host, domain: host, mailbox: null };
    }
    if (!entry) continue;

    const key = `${entry.kind}:${entry.input.toLowerCase()}`;
    if (seen.has(key)) { duplicateCount++; continue; }
    seen.add(key);
    entries.push(entry);
  }

  return { entries, duplicateCount };
}

// ── Mail provider identification ─────────────────────────────────────────────

// The single most useful thing a domain lookup gives an investigator: the MX
// records say who actually holds the mailbox, which is who legal process has to
// go to. A domain registered anywhere at all can have its mail at Google.
//
// `gateway: true` marks a filtering/security layer that sits IN FRONT of the
// real mailbox — seeing one means the actual provider is still unknown.
const MAIL_PROVIDERS = [
  { match: /(^|\.)aspmx\.l\.google\.com$|(^|\.)googlemail\.com$|(^|\.)google\.com$/, name: 'Google Workspace (Gmail)' },
  { match: /(^|\.)protection\.outlook\.com$|(^|\.)outlook\.com$|(^|\.)office365\.com$/, name: 'Microsoft 365 (Exchange Online)' },
  { match: /(^|\.)protonmail\.ch$|(^|\.)proton\.me$/, name: 'Proton Mail' },
  { match: /(^|\.)icloud\.com$|(^|\.)me\.com$|(^|\.)apple\.com$/, name: 'Apple iCloud Mail' },
  { match: /(^|\.)messagingengine\.com$|(^|\.)fastmail\.com$/, name: 'Fastmail' },
  { match: /(^|\.)zoho\.(com|eu)$/, name: 'Zoho Mail' },
  { match: /(^|\.)yahoodns\.net$/, name: 'Yahoo Mail' },
  { match: /(^|\.)yandex\.(net|ru)$/, name: 'Yandex Mail' },
  { match: /(^|\.)mail\.ru$/, name: 'Mail.ru' },
  { match: /(^|\.)qq\.com$|(^|\.)tencent\.com$/, name: 'Tencent QQ Mail' },
  { match: /(^|\.)secureserver\.net$/, name: 'GoDaddy' },
  { match: /(^|\.)registrar-servers\.com$|(^|\.)privateemail\.com$/, name: 'Namecheap Private Email' },
  { match: /(^|\.)titan\.email$/, name: 'Titan Mail' },
  { match: /(^|\.)improvmx\.com$/, name: 'ImprovMX (forwarding)' },
  { match: /(^|\.)mx\.cloudflare\.net$/, name: 'Cloudflare Email Routing (forwarding)' },
  { match: /(^|\.)ionos\.|(^|\.)1and1\./, name: 'IONOS / 1&1' },
  { match: /(^|\.)ovh\.net$/, name: 'OVH' },
  { match: /(^|\.)hostinger\./, name: 'Hostinger' },
  { match: /(^|\.)amazonaws\.com$|(^|\.)amazonses\.com$/, name: 'Amazon SES / WorkMail' },
  { match: /(^|\.)mimecast\.(com|co\.za)$/, name: 'Mimecast', gateway: true },
  { match: /(^|\.)pphosted\.com$|(^|\.)proofpoint\.com$/, name: 'Proofpoint', gateway: true },
  { match: /(^|\.)barracudanetworks\.com$/, name: 'Barracuda', gateway: true },
  { match: /(^|\.)messagelabs\.com$/, name: 'Broadcom/Symantec MessageLabs', gateway: true },
  { match: /(^|\.)trendmicro\.com$|(^|\.)tmes\.trendmicro\.com$/, name: 'Trend Micro', gateway: true },
  { match: /(^|\.)sophos\.com$/, name: 'Sophos', gateway: true },
];

// Returns { name, gateway, selfHosted } or null when nothing matches.
export function identifyMailProvider(mxHosts, domain) {
  if (!mxHosts.length) return null;
  for (const { match, name, gateway } of MAIL_PROVIDERS) {
    if (mxHosts.some(h => match.test(h))) return { name, gateway: Boolean(gateway), selfHosted: false };
  }
  // MX pointing back inside the domain itself means they run their own server.
  const suffix = `.${domain}`;
  if (mxHosts.every(h => h === domain || h.endsWith(suffix))) {
    return { name: 'Self-hosted (mail server inside this domain)', gateway: false, selfHosted: true };
  }
  return null;
}

// ── RDAP for domains ─────────────────────────────────────────────────────────

function vcardValue(entity, field) {
  const props = entity && entity.vcardArray && entity.vcardArray[1];
  if (!Array.isArray(props)) return '';
  for (const p of props) {
    if (Array.isArray(p) && p[0] === field) {
      const v = p[3];
      if (typeof v === 'string') return v;
      if (Array.isArray(v)) return v.filter(Boolean).join(', ');
    }
  }
  return '';
}

function findEntity(entities, role, depth = 0) {
  if (!Array.isArray(entities) || depth > 4) return null;
  for (const e of entities) if ((e.roles || []).includes(role)) return e;
  for (const e of entities) {
    const nested = findEntity(e.entities, role, depth + 1);
    if (nested) return nested;
  }
  return null;
}

function eventDate(events, action) {
  const hit = (events || []).find(e => e.eventAction === action);
  return hit ? String(hit.eventDate || '').slice(0, 10) : '';
}

function parseDomainRdap(json) {
  const entities = json.entities || [];
  const registrar = findEntity(entities, 'registrar');
  const registrant = findEntity(entities, 'registrant');
  const abuse = findEntity(entities, 'abuse');

  return {
    domain: (json.ldhName || '').toLowerCase(),
    handle: json.handle || '',
    status: json.status || [],
    registrarName: registrar ? (vcardValue(registrar, 'fn') || registrar.handle || '') : '',
    registrarIana: registrar ? (registrar.publicIds || []).map(p => p.identifier).join(', ') : '',
    registrantName: registrant ? (vcardValue(registrant, 'fn') || '') : '',
    registrantOrg: registrant ? (vcardValue(registrant, 'org') || '') : '',
    registrantEmail: registrant ? vcardValue(registrant, 'email') : '',
    abuseEmail: abuse ? vcardValue(abuse, 'email') : '',
    abusePhone: abuse ? vcardValue(abuse, 'tel') : '',
    registered: eventDate(json.events, 'registration'),
    expires: eventDate(json.events, 'expiration'),
    lastChanged: eventDate(json.events, 'last changed'),
    nameservers: (json.nameservers || [])
      .map(n => String(n.ldhName || '').toLowerCase().replace(/\.$/, ''))
      .filter(Boolean),
    dnssec: Boolean(json.secureDNS && json.secureDNS.delegationSigned),
  };
}

async function fetchDomainRdap(domain, signal) {
  const res = await fetch(`https://rdap.org/domain/${encodeURIComponent(domain)}`, {
    headers: { Accept: 'application/rdap+json' },
    signal,
  });
  if (res.status === 404) {
    const err = new Error(
      'No RDAP record published for this TLD. Some registries — .de, .ru and .edu among '
      + 'them — still run WHOIS only, which is a raw TCP protocol a browser cannot speak. '
      + 'Use a WHOIS client or the registry’s own web form for these.',
    );
    err.noRdap = true;
    throw err;
  }
  if (!res.ok) throw new Error(`RDAP lookup failed (HTTP ${res.status})`);
  return parseDomainRdap(await res.json());
}

// ── Orchestration ────────────────────────────────────────────────────────────

function settle(source, promise) {
  return promise.then(
    data => ({ source, data, error: null }),
    err => ({ source, data: null, error: err.name === 'AbortError' ? 'Cancelled' : (err.message || 'Failed'), noRdap: err.noRdap }),
  );
}

const answersOf = (r, type) => (r.data ? r.data.answers.filter(a => a.type === type).map(a => a.data) : []);
const stripDot = (s) => s.replace(/\.$/, '');

export async function lookupDomain(domain, { signal } = {}) {
  const [rdap, mx, a, aaaa, ns, txt, dmarc] = await Promise.all([
    settle('RDAP', fetchDomainRdap(domain, signal)),
    settle('MX', dnsQuery(domain, 'MX', signal)),
    settle('A', dnsQuery(domain, 'A', signal)),
    settle('AAAA', dnsQuery(domain, 'AAAA', signal)),
    settle('NS', dnsQuery(domain, 'NS', signal)),
    settle('TXT', dnsQuery(domain, 'TXT', signal)),
    settle('DMARC', dnsQuery(`_dmarc.${domain}`, 'TXT', signal)),
  ]);

  // "10 aspmx.l.google.com." -> { priority: 10, host: 'aspmx.l.google.com' }
  const mxRecords = answersOf(mx, 15)
    .map(d => {
      const [prio, ...rest] = d.split(/\s+/);
      return { priority: Number(prio), host: stripDot(rest.join(' ')).toLowerCase() };
    })
    .filter(r => r.host)
    .sort((x, y) => x.priority - y.priority);

  const txtRecords = answersOf(txt, 16).map(s => s.replace(/^"|"$/g, ''));
  const dmarcRecords = answersOf(dmarc, 16).map(s => s.replace(/^"|"$/g, ''));

  return {
    domain,
    rdap,
    provider: identifyMailProvider(mxRecords.map(r => r.host), domain),
    mx: { records: mxRecords, error: mx.error },
    // No MX is itself a finding: the domain cannot receive mail at all, which
    // is common for parked domains and for spoofed sender domains.
    receivesMail: mxRecords.length > 0,
    addresses: {
      v4: answersOf(a, 1),
      v6: answersOf(aaaa, 28),
      error: a.error || aaaa.error,
    },
    nameservers: answersOf(ns, 2).map(stripDot).map(s => s.toLowerCase()),
    spf: txtRecords.find(t => /^v=spf1\b/i.test(t)) || '',
    dmarc: dmarcRecords.find(t => /^v=DMARC1\b/i.test(t)) || '',
    txt: txtRecords,
    lookedUpAt: Date.now(),
  };
}

export async function lookupDomainBatch(domains, { signal, concurrency = 4, onResult } = {}) {
  const queue = [...domains];
  const worker = async () => {
    for (;;) {
      if (signal && signal.aborted) return;
      const domain = queue.shift();
      if (!domain) return;
      try {
        const result = await lookupDomain(domain, { signal });
        if (onResult) onResult(domain, result);
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
}
